import React, { useState, useEffect, useMemo } from 'react';
import { generateGA4Summary } from '../../services/geminiService';
import LoadingSpinner from '../shared/LoadingSpinner';
import InfoTip from '../shared/InfoTip';

const WhatChangedCard = ({ kpis, comparisonMode, channelData, devicesData, clientId, startDate, endDate }) => {
  const [dismissed, setDismissed] = useState(false);
  const [summaryText, setSummaryText] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Compute alerts from KPI comparison data — must be before any early return
  const alerts = useMemo(() => {
    if (comparisonMode === 'off' || !kpis) return [];
    const comp = kpis?.comparison;
    if (!comp) return [];
    const list = [];

    // Negative alerts
    if (comp.sessions_change_pct != null && comp.sessions_change_pct < -15)
      list.push({ color: 'amber', text: `Traffic Decline — sessions dropped ${Math.abs(comp.sessions_change_pct).toFixed(1)}%` });
    if (comp.bounce_rate_change_pct != null && comp.bounce_rate_change_pct > 10)
      list.push({ color: 'amber', text: `Bounce Rate Spike — up ${comp.bounce_rate_change_pct.toFixed(1)}%` });
    if (comp.total_revenue_change_pct != null && comp.total_revenue_change_pct < -20 && kpis.total_revenue != null)
      list.push({ color: 'red', text: `Revenue Drop — down ${Math.abs(comp.total_revenue_change_pct).toFixed(1)}%` });
    if ((kpis.conversions === 0 || kpis.conversions == null) && comp.conversions_change_pct != null && comp.conversions_change_pct < -99)
      list.push({ color: 'amber', text: 'Zero Conversions — none recorded this period' });

    // Positive insights
    if (comp.sessions_change_pct != null && comp.sessions_change_pct > 10)
      list.push({ color: 'emerald', text: `Traffic Growth — sessions up ${comp.sessions_change_pct.toFixed(1)}%` });
    if (comp.conversions_change_pct != null && comp.conversions_change_pct > 10 && (kpis.conversions || 0) > 0)
      list.push({ color: 'emerald', text: `Conversions Up — increased ${comp.conversions_change_pct.toFixed(1)}%` });
    if (comp.total_revenue_change_pct != null && comp.total_revenue_change_pct > 10 && kpis.total_revenue > 0)
      list.push({ color: 'emerald', text: `Revenue Growth — up ${comp.total_revenue_change_pct.toFixed(1)}%` });
    if (comp.bounce_rate_change_pct != null && comp.bounce_rate_change_pct < -5)
      list.push({ color: 'emerald', text: `Bounce Rate Improved — down ${Math.abs(comp.bounce_rate_change_pct).toFixed(1)}%` });
    if (comp.users_change_pct != null && comp.users_change_pct > 15)
      list.push({ color: 'blue', text: `User Growth — unique visitors up ${comp.users_change_pct.toFixed(1)}%` });

    return list;
  }, [kpis, comparisonMode]);

  // Call AI to generate summary — async, cached in service layer.
  // The service caches on `ga4|clientId|startDate|endDate`, so the effect only
  // needs to re-fire when those primitives change. Depending on the `kpis` /
  // `channelData` / `devicesData` objects directly would re-run this on every
  // parent render (new references each time) and re-fire the paid Gemini call
  // on a fresh range. `hasComparison` is the single primitive that gates the
  // call; the objects themselves are read inside the closure. (Same fix as the
  // CI VerdictCard.)
  const hasComparison = !!kpis?.comparison;
  useEffect(() => {
    if (comparisonMode === 'off' || !hasComparison || !clientId || !startDate || !endDate) {
      setSummaryText(null);
      return;
    }
    let cancelled = false;
    setSummaryLoading(true);
    generateGA4Summary({ clientId, startDate, endDate, kpis, channelData, devicesData })
      .then((res) => {
        if (!cancelled) {
          setSummaryText(res.success ? res.data : null);
          setSummaryLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSummaryText(null);
          setSummaryLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [clientId, startDate, endDate, comparisonMode, hasComparison]); // eslint-disable-line

  // Early return after all hooks
  if (comparisonMode === 'off' || !kpis || dismissed) return null;
  if (!summaryText && !summaryLoading && alerts.length === 0) return null;

  return (
    <div className="relative bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800/50 rounded-xl p-4">
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss what changed summary"
        title="Dismiss"
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <div className="flex items-start gap-3 pr-6">
        <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1 flex items-center">
            <span>What Changed</span>
            <InfoTip text="AI-summarized highlights for the selected range — biggest movers, alert triggers, and notable new sources. Generated from the comparison data each time the range changes." />
          </h3>
          {summaryLoading && (
            <LoadingSpinner label="Analyzing changes..." inline />
          )}
          {!summaryLoading && summaryText && <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{summaryText}</p>}
          {(() => {
            // When the paragraph summary is present, hide badges whose numbers
            // are already in it (sessions, revenue, bounce rate). Keep badges
            // that add information not covered by the summary.
            const REDUNDANT = /^(Traffic (Decline|Growth)|Revenue (Drop|Growth)|Bounce Rate (Spike|Improved))/;
            const visible = summaryText ? alerts.filter(alert => !REDUNDANT.test(alert.text)) : alerts;
            if (visible.length === 0) return null;
            return (
              <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3 pt-3 border-t border-blue-200/50 dark:border-blue-700/30">
                {visible.map((alert, index) => {
                  const dotColor = {
                    red: 'bg-red-500',
                    amber: 'bg-amber-500',
                    emerald: 'bg-emerald-500',
                    blue: 'bg-blue-400',
                  }[alert.color] || 'bg-gray-400';
                  return (
                    <span key={index} className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                      <span className={`w-2 h-2 rounded-full ${dotColor} flex-shrink-0`} />
                      {alert.text}
                    </span>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default WhatChangedCard;
